import { useEffect, useRef, useState } from "react";
import {
  Building2,
  CalendarDays,
  CheckSquare,
  Download,
  FileText,
  Flag,
  FolderKanban,
  MoreVertical,
  Package,
  Receipt,
  Sparkles,
  StickyNote,
  Trash2,
  Wallet,
  Zap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TagBadge } from "@/components/ui/TagBadge";
import { FileTypeIcon } from "./FileTypeIcon";
import { formatFileSize } from "../hooks/useMedia";
import type { DropdownItem } from "@/modules/dropdowns/types";
import { mediaNeedsAttention, type MediaFile } from "../types";

type Props = {
  file: MediaFile;
  isSelected: boolean;
  onSelect: (file: MediaFile, shiftKey?: boolean) => void;
  onOpen: (file: MediaFile) => void;
  onDownload: (file: MediaFile) => void;
  onDelete: (file: MediaFile) => void;
  onCopyLink?: (file: MediaFile) => void;
  onEdit?: (file: MediaFile) => void;
  onMoveToCollection?: (file: MediaFile) => void;
  onNewVersion?: (file: MediaFile) => void;
  tagOptions?: DropdownItem[];
};

type EntityInfo = {
  label: string;
  icon: typeof Building2;
};

function getEntityInfo(file: MediaFile): EntityInfo | null {
  if (file.projectName)
    return { label: `Project: ${file.projectName}`, icon: FolderKanban };
  if (file.productName)
    return { label: `Product: ${file.productName}`, icon: Package };
  if (file.companyName)
    return { label: `Company: ${file.companyName}`, icon: Building2 };
  if (file.meetingTitle)
    return { label: `Meeting: ${file.meetingTitle}`, icon: CalendarDays };
  if (file.taskTitle)
    return { label: `Task: ${file.taskTitle}`, icon: CheckSquare };
  if (file.actionTitle)
    return { label: `Action: ${file.actionTitle}`, icon: Zap };
  if (file.noteTitle)
    return { label: `Note: ${file.noteTitle}`, icon: StickyNote };
  if (file.milestoneTitle)
    return { label: `Milestone: ${file.milestoneTitle}`, icon: Flag };
  if (file.budgetName)
    return { label: `Budget: ${file.budgetName}`, icon: Wallet };
  if (file.expenseTitle)
    return { label: `Expense: ${file.expenseTitle}`, icon: Receipt };
  if (file.entityLabel && file.entityType)
    return { label: `${file.entityType}: ${file.entityLabel}`, icon: FileText };
  return null;
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / (24 * 3600 * 1000));
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return d.toLocaleDateString();
}

export function FileCard({
  file,
  isSelected,
  onSelect,
  onOpen,
  onDownload,
  onDelete,
  onCopyLink,
  onEdit,
  onMoveToCollection,
  onNewVersion,
  tagOptions = [],
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const entity = getEntityInfo(file);

  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [menuOpen]);

  return (
    <div
      className={cn(
        "group relative rounded-xl border bg-white p-[14px] transition-all cursor-pointer hover:shadow-md hover:-translate-y-[1px]",
        isSelected
          ? "border-primary border-2 bg-primary/5"
          : "border-border",
      )}
      onClick={() => onOpen(file)}
    >
      <div className="flex items-start justify-between gap-2">
        <FileTypeIcon fileType={file.fileType} size={40} />
        <div className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={isSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              onSelect(file, (e.nativeEvent as MouseEvent).shiftKey)
            }
            className={cn(
              "h-4 w-4 rounded border-gray-300 cursor-pointer",
              isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100",
            )}
          />
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((v) => !v);
              }}
              className="h-6 w-6 flex items-center justify-center rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Actions"
            >
              <MoreVertical size={14} />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-48 rounded-md border bg-white shadow-lg z-20 py-1 text-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <MenuItem
                  onClick={() => {
                    setMenuOpen(false);
                    onDownload(file);
                  }}
                >
                  Download
                </MenuItem>
                {onCopyLink && (
                  <MenuItem
                    onClick={() => {
                      setMenuOpen(false);
                      onCopyLink(file);
                    }}
                  >
                    Copy Link
                  </MenuItem>
                )}
                {onEdit && (
                  <MenuItem
                    onClick={() => {
                      setMenuOpen(false);
                      onEdit(file);
                    }}
                  >
                    Edit Details
                  </MenuItem>
                )}
                {onMoveToCollection && (
                  <MenuItem
                    onClick={() => {
                      setMenuOpen(false);
                      onMoveToCollection(file);
                    }}
                  >
                    Move to Collection
                  </MenuItem>
                )}
                {onNewVersion && !file.isExternal && (
                  <MenuItem
                    onClick={() => {
                      setMenuOpen(false);
                      onNewVersion(file);
                    }}
                  >
                    New Version
                  </MenuItem>
                )}
                <div className="h-px bg-border my-1" />
                <MenuItem
                  destructive
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete(file);
                  }}
                >
                  <Trash2 size={12} /> Delete
                </MenuItem>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3">
        <div
          className="text-[13px] font-semibold leading-tight line-clamp-2 flex items-start gap-1.5"
          title={file.title}
        >
          {mediaNeedsAttention(file) && (
            <span
              className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600"
              title="Missing details — click to add"
              aria-label="Missing details"
            >
              <Sparkles size={10} strokeWidth={2} />
            </span>
          )}
          <span className="min-w-0">{file.title}</span>
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span>{formatFileSize(file.fileSize)}</span>
          {file.extension && (
            <span className="rounded bg-muted px-1.5 py-0.5 uppercase text-[10px]">
              {file.extension}
            </span>
          )}
          {file.version > 1 && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px]">
              v{file.version}
            </span>
          )}
        </div>
      </div>

      {entity && (
        <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-1 text-[11px] text-muted-foreground max-w-full">
          <entity.icon size={11} className="shrink-0" />
          <span className="truncate">{entity.label}</span>
        </div>
      )}

      {file.tags && file.tags.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1">
          {file.tags.slice(0, 2).map((tag) => {
            const opt = tagOptions.find((o) => o.value === tag);
            return (
              <TagBadge
                key={tag}
                value={tag}
                label={opt?.label ?? tag}
                size="sm"
              />
            );
          })}
          {file.tags.length > 2 && (
            <span className="text-[10px] text-muted-foreground">
              +{file.tags.length - 2} more
            </span>
          )}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between border-t pt-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center text-[9px] font-semibold text-muted-foreground shrink-0">
            {(file.uploadedByName ?? "?").slice(0, 1).toUpperCase()}
          </div>
          <span className="text-[11px] text-muted-foreground truncate">
            {file.uploadedByName ?? "Unknown"}
          </span>
        </div>
        <span className="text-[11px] text-muted-foreground">
          {formatRelativeDate(file.createdAt)}
        </span>
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDownload(file);
        }}
        className="absolute bottom-2 right-2 h-6 w-6 flex items-center justify-center rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Download"
      >
        <Download size={12} />
      </button>
    </div>
  );
}

function MenuItem({
  children,
  onClick,
  destructive,
}: {
  children: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2",
        destructive && "text-destructive",
      )}
    >
      {children}
    </button>
  );
}
